import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Button, CircularProgress, Alert,
  Chip, TextField, Divider, Dialog, DialogTitle, DialogContent, DialogActions,
  Checkbox, FormControlLabel,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import RequireAuth from '../../components/athletes/RequireAuth';
import { useAuth } from '../../context/AuthContext';
import {
  listMyProgramMemberships, getProgramOwner, listProgramAdmins,
  addProgramAdmin, removeProgramAdmin, relinquishProgramAdmin, transferProgramOwnership,
} from '../../services/programService';

// "Alex, Sam, and Jo" / "Alex and Sam" / "Alex" — used to name the
// Program Admin(s) who will remain after a relinquish, in that dialog
// below.
function formatNames(names) {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

/** Owner-only — clicking "Add" opens a confirmation dialog instead of
 * granting immediately; Program Admin is a high-authority, Program-wide
 * role. Display name is deliberately NOT looked up or shown here: 025's
 * add_program_admin() resolves the target's profile and grants access
 * atomically inside one SECURITY DEFINER function — the client never
 * sees a resolved profile before that grant happens, and there is no
 * separate "look up a user by email" RPC anywhere in this codebase
 * (009's add_team_staff() docs the same deliberate omission). The
 * confirmation shows the entered email only. */
function AddProgramAdminForm({ programId, programName, onAdded }) {
  const [email, setEmail] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!email) { setError('Enter an email address.'); return; }
    setConfirmOpen(true); // no backend call yet
  };

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const result = await addProgramAdmin({ programId, email });
      setSuccess(`${result.email || email} added as a Program Admin.`);
      setEmail('');
      setConfirmOpen(false);
      await onAdded();
    } catch (e2) {
      setError(e2.message || 'Could not add Program Admin.');
      setConfirmOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a', mb: 3 }}>
        <CardContent>
          <Typography sx={{ fontWeight: 800, mb: 1.5 }}>Add Program Admin</Typography>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <TextField
              label="Email" type="email" size="small" value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="coach@example.com"
              sx={{ flex: 1, minWidth: 220 }}
            />
            <Button type="submit" variant="contained" color="primary" sx={{ textTransform: 'none', fontWeight: 700, height: 40 }}>
              Add
            </Button>
          </Box>
          <Typography sx={{ color: '#4a6080', fontSize: '0.75rem', mt: 1 }}>
            They must already have a Football for Nerds account under this email.
          </Typography>
          {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mt: 1.5 }}>{success}</Alert>}
        </CardContent>
      </Card>

      <Dialog
        open={confirmOpen}
        onClose={() => { if (!saving) setConfirmOpen(false); }}
        PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.98)', border: '1px solid #1e2a3a' } }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Add Program Administrator?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>You are about to give:</Typography>
          <Typography sx={{ fontWeight: 800, mb: 2 }}>{email}</Typography>

          <Typography sx={{ mb: 1.5 }}>delegated Program Administrator access to:</Typography>
          <Typography sx={{ fontWeight: 800, mb: 2 }}>{programName}</Typography>

          <Typography sx={{ color: '#9eb8d0', mb: 1.5 }}>
            They will be able to help manage the Program and its Season Teams. They will NOT be able to add or remove other Program Administrators, or transfer Program ownership — only the Program Owner can do that.
          </Typography>
          <Typography sx={{ color: '#9eb8d0' }}>
            This does not automatically give them Team Admin, Head Coach, or Assistant Coach roles on individual Season Teams.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={saving} sx={{ textTransform: 'none', color: '#9eb8d0' }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={saving}
            variant="contained" color="primary"
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {saving ? <CircularProgress size={18} sx={{ color: '#050c16' }} /> : 'Add Program Admin'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** Owner-only — one Remove control per delegated Admin row, each with its
 * own confirmation dialog. Destructive/red styling (this removes
 * authority, unlike Add). Never touches team_staff. */
function RemoveProgramAdminButton({ admin, programName, onRemoved }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState(null);

  const handleConfirm = async () => {
    setRemoving(true);
    setError(null);
    try {
      await removeProgramAdmin(admin.id);
      setConfirmOpen(false);
      await onRemoved();
    } catch (e) {
      setError(e.message || 'Could not remove Program Admin.');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      <Button
        size="small" color="error"
        onClick={() => setConfirmOpen(true)}
        sx={{ textTransform: 'none', fontWeight: 700 }}
      >
        Remove
      </Button>

      <Dialog
        open={confirmOpen}
        onClose={() => { if (!removing) setConfirmOpen(false); }}
        PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.98)', border: '1px solid #1e2a3a' } }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Remove Program Administrator?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontWeight: 800, mb: 1.5 }}>{admin.display_name || admin.email}</Typography>
          <Typography sx={{ mb: 1.5 }}>will lose Program Administrator access to:</Typography>
          <Typography sx={{ fontWeight: 800, mb: 2 }}>{programName}</Typography>
          <Typography sx={{ color: '#9eb8d0' }}>
            Any Team Admin, Head Coach, Assistant Coach, or other team-level roles they hold are not affected.
          </Typography>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={removing} sx={{ textTransform: 'none', color: '#9eb8d0' }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={removing}
            variant="contained" color="error"
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {removing ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Remove Program Admin'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** Owner-only — visually separated (amber/warning border, not the plain
 * card border everything else uses) since this is the highest-impact
 * action on this page. Submitting the form opens a confirmation dialog
 * — transfer_program_ownership() is only ever called from that dialog's
 * own confirm button. Only ONE retain checkbox (Program Admin) — no
 * retain-Team-Admin/HC/Assistant-Coach checkboxes, since those
 * relationships are independent of Program ownership and are never
 * touched regardless. */
function TransferOwnershipSection({ programId, programName, onTransferred }) {
  const [email, setEmail] = useState('');
  const [retain, setRetain] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    if (!email) { setError("Enter the new Owner's email address."); return; }
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setTransferring(true);
    setError(null);
    try {
      await transferProgramOwnership({ programId, targetEmail: email, retainOutgoingAsAdmin: retain });
      onTransferred({ retained: retain, targetEmail: email });
    } catch (e2) {
      setError(e2.message || 'Could not transfer ownership.');
      setConfirmOpen(false);
      setTransferring(false);
    }
    // No `finally` — on success this component navigates away.
  };

  return (
    <Box sx={{ mt: 5, pt: 3, borderTop: '2px solid #4a2a05' }}>
      <Typography variant="h6" sx={{ fontWeight: 900, color: '#f5a623', mb: 0.5 }}>
        Transfer Program Ownership
      </Typography>
      <Typography sx={{ color: '#9eb8d0', fontSize: '0.85rem', mb: 2 }}>
        This is a high-impact action. Ownership gives ultimate administrative authority over this Program. You will lose ownership after the transfer.
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth label="New Owner's email" type="email" size="small" value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="owner@example.com"
          sx={{ mb: 1 }}
        />
        <Typography sx={{ color: '#4a6080', fontSize: '0.75rem', mb: 2 }}>
          They must already have a Football for Nerds account under this email.
        </Typography>

        <FormControlLabel
          control={<Checkbox checked={retain} onChange={e => setRetain(e.target.checked)} />}
          label="Keep me as a Program Administrator after transfer"
          sx={{ display: 'block', mb: 1 }}
        />

        {error && <Alert severity="error" sx={{ mt: 1, mb: 2 }}>{error}</Alert>}

        <Button type="submit" variant="outlined" color="secondary" sx={{ textTransform: 'none', fontWeight: 700 }}>
          Transfer Ownership
        </Button>
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={() => { if (!transferring) setConfirmOpen(false); }}
        PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.98)', border: '1px solid #1e2a3a' } }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Transfer Program Ownership?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>You are about to transfer ownership of:</Typography>
          <Typography sx={{ fontWeight: 800, mb: 2 }}>{programName}</Typography>

          <Typography sx={{ mb: 1.5 }}>to:</Typography>
          <Typography sx={{ fontWeight: 800, mb: 2 }}>{email}</Typography>

          <Typography sx={{ color: '#9eb8d0', mb: 1.5 }}>
            The new Owner will control Program Administrator membership and ultimate Program authority.
          </Typography>
          <Typography sx={{ fontWeight: 700, mb: 1.5 }}>
            {retain
              ? 'You will remain a Program Administrator.'
              : 'You will no longer have Program-level administrative access after this transfer.'}
          </Typography>
          <Typography sx={{ color: '#9eb8d0' }}>
            Your Team Admin, Head Coach, Assistant Coach, and other team-level roles are not affected.
          </Typography>

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={transferring} sx={{ textTransform: 'none', color: '#9eb8d0' }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={transferring}
            variant="contained" color="error"
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {transferring ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Transfer Ownership'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function ManageProgramInner() {
  const { programId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [program, setProgram] = useState(null);
  const [owner, setOwner] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relinquishing, setRelinquishing] = useState(false);
  const [relinquishError, setRelinquishError] = useState(null);
  const [confirmRelinquishOpen, setConfirmRelinquishOpen] = useState(false);

  const load = useCallback(async () => {
    // Guards on currentUser (not just relies on RequireAuth) so this is
    // genuinely re-invoked whenever the authenticated identity changes
    // while this component stays mounted on the same URL — see
    // MyProgramsPage's own comment for the full hard-refresh reasoning
    // this mirrors.
    if (!currentUser) return;
    const [memberships, ownerRow, adminRows] = await Promise.all([
      listMyProgramMemberships(),
      getProgramOwner(programId),
      listProgramAdmins(programId),
    ]);
    setProgram(memberships.find(p => p.id === programId) || null);
    setOwner(ownerRow);
    setAdmins(adminRows);
  }, [programId, currentUser]);

  useEffect(() => {
    // Checked here too, before touching loading state at all — see
    // MyProgramsPage/ManageProgramPage's established reasoning: auth not
    // yet ready must never present as "not found."
    if (!currentUser) return;
    setLoading(true);
    load().catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [load, currentUser]);

  const isOwner = program?.viewer_role === 'owner';

  // Delegated Program Admins for display — the Owner's own overlapping
  // legacy program_admins row (025's deliberate compatibility backfill)
  // is filtered out HERE, client-side, display-only. The Owner must
  // appear exactly once, in the Program Owner section below, never
  // duplicated into this list, and never offered a "Remove" control
  // against their own overlapping row. This never mutates that row —
  // cleaning it up server-side is explicitly out of scope for this
  // phase.
  const delegatedAdmins = useMemo(
    () => admins.filter(a => a.status === 'active' && a.user_id !== owner?.user_id),
    [admins, owner]
  );
  const remainingAdminNames = useMemo(
    () => delegatedAdmins.filter(a => a.user_id !== currentUser?.id).map(a => a.display_name || a.email),
    [delegatedAdmins, currentUser]
  );

  const handleRelinquish = async () => {
    setRelinquishError(null);
    setRelinquishing(true);
    try {
      await relinquishProgramAdmin(programId);
      navigate('/my-programs', { state: { relinquished: program?.name } });
    } catch (e) {
      setRelinquishError(e.message || 'Could not relinquish Program Admin role.');
      setRelinquishing(false);
    }
    // No `finally` — on success this component navigates away.
  };

  const handleTransferred = ({ retained, targetEmail }) => {
    if (retained) {
      navigate(`/my-programs/${programId}`, { state: { transferredOwnershipTo: targetEmail } });
    } else {
      navigate('/my-programs', { state: { transferredProgram: program?.name } });
    }
  };

  if (loading) return <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress sx={{ color: '#2ea84b' }} /></Box>;
  if (error) return <Container maxWidth="sm" sx={{ py: 6 }}><Alert severity="error">{error}</Alert></Container>;
  if (!program) return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Alert severity="error">Program not found, or you don't have Program-level access to it.</Alert>
    </Container>
  );

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Button onClick={() => navigate(`/my-programs/${programId}`)} sx={{ textTransform: 'none', color: '#6a8aa8', mb: 1, pl: 0 }}>
        ← {program.name}
      </Button>
      <Typography variant="h4" sx={{ fontWeight: 900, mb: 3 }}>Manage Program</Typography>

      <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a', mb: 3 }}>
        <CardContent>
          <Typography sx={{ fontWeight: 800, mb: 1.5 }}>Program Owner</Typography>
          <Divider sx={{ mb: 1.5 }} />
          {owner ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Typography sx={{ fontWeight: 600 }}>
                  {owner.display_name || owner.email}
                  {owner.user_id === currentUser?.id && (
                    <Typography component="span" sx={{ color: '#6a8aa8', fontSize: '0.8rem', ml: 0.75 }}>(you)</Typography>
                  )}
                </Typography>
                {owner.display_name && (
                  <Typography sx={{ color: '#6a8aa8', fontSize: '0.8rem' }}>{owner.email}</Typography>
                )}
              </Box>
              <Chip size="small" label="Program Owner" sx={{ bgcolor: '#2a1a05', color: '#f5a623', fontWeight: 700 }} />
            </Box>
          ) : (
            <Typography sx={{ color: '#6a8aa8' }}>Owner information unavailable.</Typography>
          )}
        </CardContent>
      </Card>

      {isOwner && <AddProgramAdminForm programId={programId} programName={program.name} onAdded={load} />}

      <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
        <CardContent>
          <Typography sx={{ fontWeight: 800, mb: 1.5 }}>Program Administrators</Typography>
          <Divider sx={{ mb: 1.5 }} />
          {delegatedAdmins.length === 0 ? (
            <Typography sx={{ color: '#6a8aa8' }}>No Program Administrators yet.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {delegatedAdmins.map(a => (
                <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Typography sx={{ fontWeight: 600 }}>
                    {a.display_name || a.email}
                    {a.user_id === currentUser?.id && (
                      <Typography component="span" sx={{ color: '#6a8aa8', fontSize: '0.8rem', ml: 0.75 }}>(you)</Typography>
                    )}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip size="small" label="Program Admin" sx={{ bgcolor: '#0a2a14', color: '#2ea84b', fontWeight: 700 }} />
                    {isOwner && <RemoveProgramAdminButton admin={a} programName={program.name} onRemoved={load} />}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Program Admin only — hidden entirely for Owner, not merely
          disabled. Owner leaves ownership only through Transfer
          Ownership below. No longer gated on "last admin" — 025 dropped
          that invariant (a Program may validly have zero delegated
          Admins as long as it has its one Owner), so this can always
          succeed for an ordinary active admin. */}
      {!isOwner && (
        <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid #1e2a3a' }}>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>Relinquish My Program Admin Role</Typography>
          <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem', mb: 2 }}>
            You will no longer be a Program Admin for this Program. Any Team Admin, Head Coach, or Assistant Coach roles you hold on Season Teams are not affected.
          </Typography>
          {relinquishError && <Alert severity="error" sx={{ mb: 2 }}>{relinquishError}</Alert>}
          <Button
            onClick={() => setConfirmRelinquishOpen(true)}
            disabled={relinquishing}
            variant="outlined" color="secondary"
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Relinquish My Program Admin Role
          </Button>
        </Box>
      )}

      {!isOwner && (
        <Dialog
          open={confirmRelinquishOpen}
          onClose={() => { if (!relinquishing) setConfirmRelinquishOpen(false); }}
          PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.98)', border: '1px solid #1e2a3a' } }}
        >
          <DialogTitle sx={{ fontWeight: 900 }}>Relinquish Program Admin Role?</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 1.5 }}>You will lose administrative access to:</Typography>
            <Typography sx={{ fontWeight: 800, mb: 2 }}>{program.name}</Typography>

            <Typography sx={{ color: '#9eb8d0', mb: 1.5 }}>
              Your Team Admin, Head Coach, and Assistant Coach roles on Season Teams will not be affected.
            </Typography>

            {owner && (
              <Typography sx={{ color: '#9eb8d0', mb: 1.5 }}>
                {owner.display_name || owner.email} remains the Program Owner.
              </Typography>
            )}
            {remainingAdminNames.length > 0 && (
              <Typography sx={{ color: '#9eb8d0', mb: 1.5 }}>
                {formatNames(remainingAdminNames)} will also remain Program Administrator{remainingAdminNames.length === 1 ? '' : 's'}.
              </Typography>
            )}

            <Typography sx={{ color: '#9eb8d0' }}>
              You can only regain Program Admin access if the Program Owner adds you again.
            </Typography>

            {relinquishError && <Alert severity="error" sx={{ mt: 2 }}>{relinquishError}</Alert>}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={() => setConfirmRelinquishOpen(false)} disabled={relinquishing} sx={{ textTransform: 'none', color: '#9eb8d0' }}>
              Cancel
            </Button>
            <Button
              onClick={handleRelinquish}
              disabled={relinquishing}
              variant="contained" color="error"
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              {relinquishing ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Relinquish Program Admin Role'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {isOwner && (
        <TransferOwnershipSection programId={programId} programName={program.name} onTransferred={handleTransferred} />
      )}
    </Container>
  );
}

export default function ManageProgramPage() {
  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navbar />
      <RequireAuth><ManageProgramInner /></RequireAuth>
    </Box>
  );
}
